<template>
  <div>
    <component :is="embedded ? 'div' : 'b-card'" :no-body="!embedded" class="mb-0">
      <div class="leads-header" :class="{ 'leads-header--embedded': embedded }">
        <div class="d-flex align-items-center" style="gap:10px">
          <h5 v-if="!embedded" class="mb-0 font-weight-bold">Leads</h5>
          <b-badge v-if="newCount" variant="light-warning">{{ newCount }} new</b-badge>
        </div>

        <div class="d-flex align-items-center flex-wrap" style="gap:8px">
          <b-form-select
            v-model="statusFilter"
            :options="statusFilterOptions"
            size="sm"
            class="w-auto"
            :disabled="loading"
            @change="loadLeads"
          />
          <b-form-select
            v-model="channelFilter"
            :options="channelFilterOptions"
            size="sm"
            class="w-auto"
            :disabled="loading"
            @change="loadLeads"
          />
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon"
            :disabled="loading"
            v-b-tooltip.hover
            title="Refresh"
            @click="loadLeads"
          >
            <feather-icon icon="RefreshCwIcon" size="15" :class="loading ? 'spin' : ''" />
          </b-button>
        </div>
      </div>

      <b-table
        :items="displayLeads"
        :fields="fields"
        :busy="loading"
        :per-page="embedded ? 0 : perPage"
        :current-page="currentPage"
        responsive
        hover
        show-empty
        empty-text="No leads yet."
        class="mb-0 leads-table"
      >
        <template #table-busy>
          <div class="text-center py-3">
            <b-spinner small variant="primary" />
          </div>
        </template>

        <template #cell(customer)="{ item }">
          <div class="d-flex align-items-center" style="gap:10px">
            <b-avatar :src="item.picture_url || null" :text="avatarInitial(item)" size="34" variant="light-primary" />
            <div style="min-width:0">
              <div class="font-weight-bold text-truncate" style="max-width:180px">
                {{ item.name || item.display_name || 'Unknown' }}
              </div>
              <div class="user-id-line">
                <small class="text-muted text-truncate">{{ item.user_id }}</small>
                <b-button
                  v-if="item.user_id"
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon user-id-copy"
                  v-b-tooltip.hover
                  :title="item._idCopied ? 'Copied!' : 'Copy user ID'"
                  @click="copyUserId(item)"
                >
                  <feather-icon :icon="item._idCopied ? 'CheckIcon' : 'CopyIcon'" size="12" />
                </b-button>
              </div>
            </div>
          </div>
        </template>

        <template #cell(contact)="{ item }">
          <div class="contact-lines">
            <div v-if="item.phone"><feather-icon icon="PhoneIcon" size="12" /> {{ item.phone }}</div>
            <div v-if="item.line_id"><feather-icon icon="MessageCircleIcon" size="12" /> {{ item.line_id }}</div>
            <div v-if="item.email"><feather-icon icon="MailIcon" size="12" /> {{ item.email }}</div>
            <span v-if="!item.phone && !item.line_id && !item.email" class="text-muted">—</span>
          </div>
        </template>

        <template #cell(note)="{ item }">
          <span v-if="item.note" class="note-cell">{{ item.note }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(properties)="{ item }">
          <template v-if="item.properties && item.properties.length">
            <b-button
              variant="flat-secondary"
              size="sm"
              class="props-toggle"
              @click="toggleDetails(item)"
            >
              <feather-icon icon="HomeIcon" size="13" />
              <span class="mx-25">{{ propSummary(item) }}</span>
              <feather-icon :icon="item._showDetails ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="13" />
            </b-button>
          </template>
          <span v-else class="text-muted">—</span>
        </template>

        <template #row-details="{ item }">
          <div class="prop-details">
            <div v-if="needsFollowup(item) && item._followup" class="followup-panel">
              <div class="followup-head">
                <feather-icon icon="PhoneCallIcon" size="14" />
                <span class="followup-title">Owner follow-up</span>
                <span class="followup-hint">— the availability email to an owner failed; contact them directly and log the outcome.</span>
                <span v-if="item.owner_followup_by || item.owner_followup_at" class="followup-audit">
                  last update
                  <template v-if="item.owner_followup_by">by {{ item.owner_followup_by }}</template>
                  <template v-if="item.owner_followup_at"> · {{ fromNow(item.owner_followup_at) }}</template>
                </span>
              </div>
              <div class="followup-controls">
                <b-form-select
                  v-model="item._followup.status"
                  :options="followupStatusOptions"
                  size="sm"
                  class="followup-select"
                />
                <b-form-input
                  v-model="item._followup.note"
                  size="sm"
                  maxlength="500"
                  placeholder="Note — who you spoke to, outcome, next step…"
                  class="followup-note"
                  @keyup.enter="saveFollowup(item)"
                />
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="item._followupSaving || !followupDirty(item)"
                  @click="saveFollowup(item)"
                >
                  <b-spinner v-if="item._followupSaving" small />
                  <span v-else>Save</span>
                </b-button>
              </div>
            </div>

            <div v-if="item._ownersLoading" class="owners-loading">
              <b-spinner small /> Loading owner contact…
            </div>

            <div
              v-for="p in sortedProps(item)"
              :key="p.property_id"
              class="prop-card"
              :class="{ 'prop-card--interested': p.kind === 'interested' }"
            >
              <div class="prop-card-head">
                <router-link
                  v-if="p.property_id"
                  :to="{ name: 'property-edit', params: { id: p.property_id } }"
                  target="_blank"
                  class="prop-code"
                  :class="{ 'prop-code--muted': !propCode(p) }"
                  v-b-tooltip.hover
                  title="Open property detail"
                >{{ propCode(p) || ('#' + p.property_id) }}</router-link>
                <span
                  v-else
                  class="prop-code prop-code--muted"
                  v-b-tooltip.hover
                  title="Property code"
                >—</span>

                <a
                  v-if="p.url"
                  :href="p.url"
                  target="_blank"
                  rel="noopener"
                  class="prop-name text-truncate"
                >{{ p.name || 'Untitled property' }}</a>
                <span v-else class="prop-name text-truncate">{{ p.name || 'Untitled property' }}</span>

                <span
                  class="prop-chip prop-chip--sm"
                  :class="{ 'prop-chip--stale': isStaleListing(p) }"
                  v-b-tooltip.hover
                  :title="isStaleListing(p)
                    ? `Listing not refreshed in over ${staleListingMonths} months — owner availability email triggered`
                    : 'Listing last refreshed'"
                >
                  <feather-icon :icon="isStaleListing(p) ? 'AlertTriangleIcon' : 'ClockIcon'" size="11" />
                  Updated
                  <template v-if="propUpdatedAt(p)">{{ fmtDate(propUpdatedAt(p)) }} ({{ fromNow(propUpdatedAt(p)) }})</template>
                  <template v-else>— unknown</template>
                </span>
                <span
                  v-if="propAvailableDate(p)"
                  class="prop-chip prop-chip--sm"
                >
                  <feather-icon icon="CalendarIcon" size="11" /> Available {{ fmtDate(propAvailableDate(p)) }}
                </span>

                <b-badge
                  :variant="p.kind === 'interested' ? 'light-primary' : 'light-secondary'"
                  class="prop-kind"
                >
                  {{ p.kind === 'interested' ? 'Interested' : 'Similar' }}
                </b-badge>

                <b-badge
                  :variant="emailStatusVariant(p)"
                  class="prop-email"
                  v-b-tooltip.hover
                  :title="emailStatusHint(p)"
                >
                  <feather-icon icon="MailIcon" size="10" />
                  {{ emailStatusLabel(p) }}
                  <span
                    v-if="p.owner_email_requested_at"
                    v-b-tooltip.hover
                    :title="formatFull(p.owner_email_requested_at)"
                  >· {{ fromNow(p.owner_email_requested_at) }}</span>
                </b-badge>
              </div>

              <div class="prop-card-meta">
                <span v-if="propBedroom(p) != null" class="prop-chip" v-b-tooltip.hover title="Bedrooms">
                  <feather-icon icon="HomeIcon" size="11" /> {{ propBedroom(p) }} BR
                </span>
                <span v-if="propSqm(p) != null" class="prop-chip" v-b-tooltip.hover title="Usable area">
                  <feather-icon icon="Maximize2Icon" size="11" /> {{ propSqm(p) }} sqm
                </span>
                <span v-if="propSalePrice(p)" class="prop-chip prop-chip--sale">
                  <feather-icon icon="TagIcon" size="11" /> Sale {{ propSalePrice(p) }}
                </span>
                <span v-if="propRentPrice(p)" class="prop-chip prop-chip--rent">
                  <feather-icon icon="RepeatIcon" size="11" /> Rent {{ propRentPrice(p) }}/mo
                </span>
                <span v-if="!propSalePrice(p) && !propRentPrice(p) && propPrice(p)" class="prop-chip prop-chip--sale">
                  {{ propPrice(p) }}
                </span>
                <span
                  v-if="propBedroom(p) == null && propSqm(p) == null && !propSalePrice(p) && !propRentPrice(p) && !propPrice(p)"
                  class="prop-chip prop-chip--empty"
                >
                  No room detail
                </span>
              </div>

              <div v-if="hasOwnerInfo(p)" class="prop-card-owner">
                <feather-icon icon="UserIcon" size="11" class="prop-owner-ico" />
                <router-link
                  v-if="ownerId(p)"
                  :to="{ name: 'clients-edit', params: { id: ownerId(p) } }"
                  target="_blank"
                  class="prop-owner-name prop-owner-name--link"
                >{{ ownerName(p) || 'Owner' }}</router-link>
                <span v-else-if="ownerName(p)" class="prop-owner-name">{{ ownerName(p) }}</span>
                <span v-else class="prop-owner-name text-muted">Owner</span>
                <span
                  v-if="ownerPhone(p)"
                  class="prop-owner-has"
                  v-b-tooltip.hover
                  title="Phone on file"
                >
                  <feather-icon icon="PhoneIcon" size="11" />
                </span>
                <span
                  v-if="ownerEmail(p)"
                  class="prop-owner-has"
                  v-b-tooltip.hover
                  title="Email on file"
                >
                  <feather-icon icon="MailIcon" size="11" />
                </span>
                <span
                  v-if="ownerLine(p)"
                  class="prop-owner-has"
                  v-b-tooltip.hover
                  title="LINE on file"
                >
                  <feather-icon icon="MessageCircleIcon" size="11" />
                </span>
              </div>
              <div v-else-if="item._ownersLoaded" class="prop-card-owner prop-card-owner--empty">
                <feather-icon icon="UserIcon" size="11" class="prop-owner-ico" />
                <span class="text-muted">No owner contact on file</span>
              </div>
            </div>
          </div>
        </template>

        <template #cell(channel)="{ item }">
          <b-badge :variant="item.channel === 'web' ? 'light-info' : 'light-success'">
            {{ item.channel === 'web' ? 'Web' : 'LINE' }}
          </b-badge>
        </template>

        <template #cell(status)="{ item }">
          <b-form-select
            :value="item.status"
            :options="statusOptions"
            size="sm"
            class="status-select"
            :class="`status-select--${item.status}`"
            @change="setStatus(item, $event)"
          />
        </template>

        <template #cell(updated_at)="{ item }">
          <span v-b-tooltip.hover :title="formatFull(item.updated_at)">{{ fromNow(item.updated_at) }}</span>
        </template>

        <template #cell(actions)="{ item }">
          <b-button variant="flat-primary" size="sm" @click="openChat(item)">
            View chat
          </b-button>
        </template>
      </b-table>

      <div v-if="embedded && !loading && leads.length > previewLimit" class="leads-show-all">
        <b-button variant="flat-primary" size="sm" :to="{ name: 'ai-leads' }">
          Show all {{ leads.length }} leads
          <feather-icon icon="ArrowRightIcon" size="14" />
        </b-button>
      </div>

      <div v-if="!embedded && !loading && leads.length > perPage" class="leads-pagination">
        <small class="text-muted">
          {{ pageRangeStart }}–{{ pageRangeEnd }} of {{ leads.length }}
        </small>
        <b-pagination
          v-model="currentPage"
          :total-rows="leads.length"
          :per-page="perPage"
          align="center"
          size="sm"
          class="mb-0"
        />
      </div>
    </component>
  </div>
</template>

<script>
import moment from 'moment'
import chatbotAxios from '@/libs/chatbot-axios'
import apiAxios from '@axios'
import {
  BCard, BTable, BBadge, BButton, BSpinner, BAvatar, BFormSelect, BFormInput, BPagination, VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const STATUSES = ['new', 'contacted', 'closed']

const STALE_LISTING_MONTHS = 3
const FOLLOWUP_STATUS_OPTIONS = [
  { value: 'pending', text: 'Pending' },
  { value: 'in_progress', text: 'In progress' },
  { value: 'reached', text: 'Reached owner' },
  { value: 'unreachable', text: "Can't reach" },
  { value: 'resolved', text: 'Resolved' },
]

export default {
  components: {
    BCard, BTable, BBadge, BButton, BSpinner, BAvatar, BFormSelect, BFormInput, BPagination,
  },
  directives: { 'b-tooltip': VBTooltip },

  emits: ['update:total'],

  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      leads: [],
      loading: false,
      previewLimit: 3,
      perPage: 10,
      currentPage: 1,
      statusFilter: 'new',
      channelFilter: '',
      sseAbort: null,

      fields: [
        { key: 'customer', label: 'Customer' },
        { key: 'contact', label: 'Contact' },
        { key: 'note', label: 'Wants' },
        { key: 'properties', label: 'Properties', class: 'text-center' },
        { key: 'channel', label: 'Channel', class: 'text-center' },
        { key: 'status', label: 'Status', class: 'text-center' },
        { key: 'updated_at', label: 'Updated' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
      followupStatusOptions: FOLLOWUP_STATUS_OPTIONS,
      staleListingMonths: STALE_LISTING_MONTHS,
      statusOptions: STATUSES.map(s => ({ value: s, text: s[0].toUpperCase() + s.slice(1) })),
      statusFilterOptions: [
        { value: '', text: 'All statuses' },
        ...STATUSES.map(s => ({ value: s, text: s[0].toUpperCase() + s.slice(1) })),
      ],
      channelFilterOptions: [
        { value: '', text: 'All channels' },
        { value: 'line', text: 'LINE' },
        { value: 'web', text: 'Web' },
      ],
    }
  },

  computed: {
    newCount() {
      return this.leads.filter(l => l.status === 'new').length
    },
    displayLeads() {
      return this.embedded ? this.leads.slice(0, this.previewLimit) : this.leads
    },
    pageRangeStart() {
      return this.leads.length ? (this.currentPage - 1) * this.perPage + 1 : 0
    },
    pageRangeEnd() {
      return Math.min(this.currentPage * this.perPage, this.leads.length)
    },
  },

  watch: {
    newCount: {
      immediate: true,
      handler(n) {
        this.$emit('update:total', n)
      },
    },
  },

  mounted() {
    this.loadLeads()
    this.startSSE()
  },

  beforeDestroy() {
    this.stopSSE()
  },

  methods: {
    async loadLeads() {
      this.loading = true
      try {
        const params = { limit: 200 }
        if (this.statusFilter) params.status = this.statusFilter
        if (this.channelFilter) params.channel = this.channelFilter
        const { data } = await chatbotAxios.get('/api/v1/leads', { params })
        this.leads = data.data || data || []
        this.currentPage = 1
      } catch (e) {
        this.showToast(`Failed to load leads: ${e.message}`, 'danger')
      } finally {
        this.loading = false
      }
    },

    async setStatus(lead, status) {
      const prev = lead.status
      if (status === prev) return
      const idx = this.leads.findIndex(l => l.user_id === lead.user_id)
      if (idx !== -1) this.$set(this.leads, idx, { ...lead, status })
      try {
        await chatbotAxios.post(`/api/v1/leads/${lead.user_id}/status`, { status })
        this.showToast(`Marked ${lead.name || 'lead'} as ${status}`)
        // Drop it from a filtered view it no longer belongs in.
        if (this.statusFilter && this.statusFilter !== status) {
          this.leads = this.leads.filter(l => l.user_id !== lead.user_id)
        }
      } catch (e) {
        if (idx !== -1) this.$set(this.leads, idx, { ...lead, status: prev })
        this.showToast(`Failed to update status: ${e.message}`, 'danger')
      }
    },

    openChat(lead) {
      const name = lead.channel === 'web' ? 'web-chat' : 'chat-bot'
      this.$router.push({ name, query: { user: lead.user_id } }).catch(() => {})
    },

    startSSE() {
      const baseURL = process.env.VUE_APP_CHATBOT_URL || 'http://localhost:3000'
      const secret = process.env.VUE_APP_CHATBOT_SECRET || ''
      const controller = new AbortController()
      this.sseAbort = controller

      fetch(`${baseURL}/api/v1/stream`, {
        headers: { 'x-admin-secret': secret },
        signal: controller.signal,
      }).then(res => {
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        const pump = () => reader.read().then(({ done, value }) => {
          if (done || controller.signal.aborted) return
          decoder.decode(value, { stream: true }).split('\n').forEach(line => {
            if (!line.startsWith('data:')) return
            try {
              this.handleSSEEvent(JSON.parse(line.slice(5).trim()))
            } catch (_) { /* partial / non-JSON line */ }
          })
          pump()
        }).catch(() => {})
        pump()
      }).catch(() => {})
    },

    stopSSE() {
      if (this.sseAbort) {
        this.sseAbort.abort()
        this.sseAbort = null
      }
    },

    handleSSEEvent(event) {
      if (event.type === 'lead_status') {
        const idx = this.leads.findIndex(l => l.user_id === event.userId)
        if (idx !== -1) {
          if (this.statusFilter && this.statusFilter !== event.status) {
            this.leads = this.leads.filter(l => l.user_id !== event.userId)
          } else {
            this.$set(this.leads, idx, { ...this.leads[idx], status: event.status })
          }
        }
        return
      }

      if (event.type === 'lead_followup') {
        const idx = this.leads.findIndex(l => l.user_id === event.userId)
        if (idx !== -1) {
          const cur = this.leads[idx]
          // keep the open editor in step unless the admin has unsaved edits
          // eslint-disable-next-line no-underscore-dangle
          const draft = cur._followup && !this.followupDirty(cur)
            ? { status: event.status, note: event.note || '' }
            : cur._followup // eslint-disable-line no-underscore-dangle
          this.$set(this.leads, idx, {
            ...cur,
            owner_followup_status: event.status,
            owner_followup_note: event.note,
            owner_followup_by: event.by,
            owner_followup_at: new Date().toISOString(),
            _followup: draft,
          })
        }
        return
      }

      if (event.type !== 'lead') return

      // A capture on a conversation we already show — refresh its details in place.
      const idx = this.leads.findIndex(l => l.user_id === event.userId)
      if (idx !== -1) {
        this.$set(this.leads, idx, {
          ...this.leads[idx],
          name: event.name || this.leads[idx].name,
          phone: event.phone || this.leads[idx].phone,
          line_id: event.line_id || this.leads[idx].line_id,
          email: event.email || this.leads[idx].email,
          note: event.note || this.leads[idx].note,
          properties: event.properties || this.leads[idx].properties || [],
          /* eslint-disable no-underscore-dangle */
          _showDetails: this.leads[idx]._showDetails,
          _ownersLoaded: event.properties ? false : this.leads[idx]._ownersLoaded,
          /* eslint-enable no-underscore-dangle */
          status: 'new',
          updated_at: event.created_at,
        })

        if (event.properties && this.leads[idx]._showDetails) this.enrichOwners(this.leads[idx])
        this.bumpToTop(event.userId)
      } else if (!this.channelFilter || this.channelFilter === event.channel) {
        this.loadLeads()
      }

      this.showToast(`${event.is_new ? 'New lead' : 'Lead updated'}: ${event.name || event.phone || event.line_id || event.email || 'contact shared'}`)
    },

    bumpToTop(userId) {
      const idx = this.leads.findIndex(l => l.user_id === userId)
      if (idx > 0) {
        const [row] = this.leads.splice(idx, 1)
        this.leads.unshift(row)
      }
    },

    // ── properties detail ───────────────────────────────────────────────
    toggleDetails(item) {
      const show = !item._showDetails
      this.$set(item, '_showDetails', show)
      if (!show) return

      if (!item._followup) {
        this.$set(item, '_followup', {
          status: item.owner_followup_status || 'pending',
          note: item.owner_followup_note || '',
        })
      }

      if (!item._ownersLoaded && !item._ownersLoading) this.enrichOwners(item)
    },

    async enrichOwners(item) {
      const ids = (item.properties || []).map(p => p.property_id).filter(Boolean)
      if (!ids.length) { this.$set(item, '_ownersLoaded', true); return }
      this.$set(item, '_ownersLoading', true)
      try {
        const { data } = await apiAxios.post('/property/v1/owners/by-property', { propertyIds: ids })
        const list = data.data || data || []
        const byId = {}
        list.forEach(o => { byId[o.property_id] = o })
        item.properties.forEach(p => {
          const o = byId[p.property_id]
          if (!o) return
          this.$set(p, 'owner_id', o.owner_id != null ? o.owner_id : o.id)
          this.$set(p, 'owner_name', o.owner_name)
          this.$set(p, 'owner_phone', o.owner_phone)
          this.$set(p, 'owner_email', o.owner_email)
          // refresh the listing-date snapshot with the current values
          if (o.listing_updated_at !== undefined) this.$set(p, 'listing_updated_at', o.listing_updated_at)
          if (o.available_date !== undefined) this.$set(p, 'available_date', o.available_date)
        })
        this.$set(item, '_ownersLoaded', true)
      } catch (e) {
        this.showToast(`Couldn't load owner contact: ${e.response?.data?.message || e.message}`, 'danger')
      } finally {
        this.$set(item, '_ownersLoading', false)
      }
    },

    // ── owner follow-up tracking ────────────────────────────────────────
    needsFollowup(item) {
      return (item.properties || []).some(p => p.owner_email_status === 'failed')
    },
    followupDirty(item) {
      // eslint-disable-next-line no-underscore-dangle
      const d = item._followup
      if (!d) return false
      return d.status !== (item.owner_followup_status || 'pending')
        || (d.note || '') !== (item.owner_followup_note || '')
    },
    async saveFollowup(item) {
      // eslint-disable-next-line no-underscore-dangle
      const draft = item._followup
      if (!draft || !this.followupDirty(item)) return
      const by = this.currentAdmin()
      this.$set(item, '_followupSaving', true)
      try {
        await chatbotAxios.post(`/api/v1/leads/${item.user_id}/owner-followup`, {
          status: draft.status,
          note: draft.note,
          by,
        })
        this.$set(item, 'owner_followup_status', draft.status)
        this.$set(item, 'owner_followup_note', draft.note)
        this.$set(item, 'owner_followup_by', by)
        this.$set(item, 'owner_followup_at', new Date().toISOString())
        this.showToast('Owner follow-up updated')
      } catch (e) {
        this.showToast(`Failed to save follow-up: ${e.message}`, 'danger')
      } finally {
        this.$set(item, '_followupSaving', false)
      }
    },
    currentAdmin() {
      try {
        const u = JSON.parse(localStorage.getItem('userData') || '{}')
        return u.fullName || u.username || u.email || 'admin'
      } catch (e) {
        return 'admin'
      }
    },

    // Interested (exact match) rows first, similar after.
    sortedProps(item) {
      return [...(item.properties || [])].sort(
        (a, b) => (a.kind === 'interested' ? 0 : 1) - (b.kind === 'interested' ? 0 : 1),
      )
    },

    // ── per-property room detail (tolerant of the field names the leads API uses) ──
    propCode(p) {
      return p.property_code || p.code || p.ref_code || ''
    },
    propBedroom(p) {
      const b = [p.bedroom, p.bedrooms, p.beds].find(v => v != null && v !== '')
      return b == null ? null : b
    },
    propSqm(p) {
      const s = [p.sqm, p.area, p.size, p.usable_area].find(v => v != null && v !== '')
      if (s == null) return null
      return typeof s === 'number' ? s.toLocaleString('en-US') : s
    },
    propPrice(p) {
      if (p.price) return p.price
      const n = p.price_thb != null ? p.price_thb : p.price_value
      return n != null ? `฿${Number(n).toLocaleString('en-US')}` : ''
    },
    fmtThb(v) {
      if (v == null || v === '' || Number(v) === 0) return ''
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return `฿${n.toLocaleString('en-US')}`
    },
    propSalePrice(p) {
      const v = [p.sale_price, p.price_sale, p.sale_price_thb, p.sell_price].find(x => x != null && x !== '')
      return this.fmtThb(v)
    },
    propRentPrice(p) {
      const v = [p.rent_price, p.price_rent, p.rent_price_thb, p.rent_month, p.monthly_rent].find(x => x != null && x !== '')
      return this.fmtThb(v)
    },

    propUpdatedAt(p) {
      return p.listing_updated_at || p.updated_at || null
    },
    propAvailableDate(p) {
      return p.available_date || p.availble_date || p.availableDate || null
    },
    isStaleListing(p) {
      const u = this.propUpdatedAt(p)
      if (!u) return true
      return moment(u).isBefore(moment().subtract(STALE_LISTING_MONTHS, 'months'))
    },
    fmtDate(raw) {
      return raw ? moment(raw).format('DD MMM YYYY') : ''
    },

    // ── per-property owner contact (tolerant of field names / array shapes) ──
    firstOf(...vals) {
      const flat = vals.reduce((acc, v) => acc.concat(v), [])
      const hit = flat.find(x => x != null && x !== '')
      return hit == null ? '' : hit
    },
    ownerName(p) {
      return this.firstOf(p.owner_name, p.owner_fullname, p.owner && p.owner.name, p.contact_name)
    },
    ownerPhone(p) {
      return this.firstOf(p.owner_phone, p.owner_tel, p.owner_mobile, p.owner_phones, p.owner && p.owner.phone)
    },
    ownerEmail(p) {
      return this.firstOf(p.owner_email, p.owner_emails, p.owner && p.owner.email)
    },
    ownerLine(p) {
      return this.firstOf(p.owner_line_id, p.owner_line, p.owner && p.owner.line_id)
    },
    ownerId(p) {
      return this.firstOf(p.owner_id, p.ownerId, p.owner && p.owner.id) || null
    },
    hasOwnerInfo(p) {
      return !!(this.ownerName(p) || this.ownerPhone(p) || this.ownerEmail(p) || this.ownerLine(p))
    },

    propSummary(item) {
      const props = item.properties || []
      const interested = props.filter(p => p.kind === 'interested').length
      const similar = props.length - interested
      const parts = []
      if (interested) parts.push(`${interested} interested`)
      if (similar) parts.push(`${similar} similar`)
      return parts.join(' · ') || String(props.length)
    },

    effectiveEmailStatus(p) {
      const s = p.owner_email_status
      if (s === 'requested' || s === 'failed') return s
      if (s === 'pending') return 'scheduled'
      if (this.ownerEmail(p) && this.isStaleListing(p)) return 'scheduled'
      return 'none'
    },

    emailStatusVariant(p) {
      return {
        requested: 'light-success',
        failed: 'light-danger',
        scheduled: 'light-warning',
        none: 'light-secondary',
      }[this.effectiveEmailStatus(p)] || 'light-secondary'
    },

    emailStatusLabel(p) {
      return {
        requested: 'Owner emailed',
        failed: 'Email failed',
        scheduled: 'Scheduled',
        none: 'No owner email',
      }[this.effectiveEmailStatus(p)] || '—'
    },

    // Tooltip copy for the per-property email badge.
    emailStatusHint(p) {
      return {
        requested: 'Availability email sent to the owner',
        failed: 'Availability email to the owner failed — see owner follow-up',
        scheduled: 'Availability email queued — the nightly job sends it to the owner tonight',
        none: 'No availability email — owner has no email on file',
      }[this.effectiveEmailStatus(p)] || ''
    },

    // ── helpers ─────────────────────────────────────────────────────────
    async copyUserId(item) {
      if (!item.user_id) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(item.user_id)
        } else {
          const ta = document.createElement('textarea')
          ta.value = item.user_id
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$set(item, '_idCopied', true)
        this.showToast('User ID copied')
        setTimeout(() => this.$set(item, '_idCopied', false), 1500)
      } catch (e) {
        this.showToast(`Couldn't copy: ${e.message}`, 'danger')
      }
    },
    avatarInitial(item) {
      const s = item.name || item.display_name || item.user_id || '?'
      return s[0].toUpperCase()
    },
    fromNow(raw) {
      return raw ? moment(raw).fromNow() : '—'
    },
    formatFull(raw) {
      return raw ? moment(raw).format('DD MMM YYYY, HH:mm') : ''
    },
    showToast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.leads-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #ebe9f1;

  &--embedded {
    padding: 0 0 12px;
    border-bottom: none;
    justify-content: flex-end;
  }
}

.leads-show-all {
  display: flex;
  justify-content: center;
  padding: 10px 0 2px;
  border-top: 1px solid #ebe9f1;
}

.leads-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #ebe9f1;
}

.leads-table {
  font-size: 0.9rem;

  ::v-deep td {
    vertical-align: middle;
  }
}

.contact-lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.82rem;
  white-space: nowrap;
}

.note-cell {
  display: inline-block;
  max-width: 260px;
  white-space: normal;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.user-id-line {
  display: flex;
  align-items: center;
  gap: 2px;
  max-width: 180px;

  small { min-width: 0; }
}

.user-id-copy {
  flex: 0 0 auto;
  padding: 2px !important;
  color: #a8a5b8;

  &:hover { color: #80AC03; }
}

.status-select {
  width: 120px;
  font-weight: 600;

  &--new { color: #ff9f43; }
  &--contacted { color: #7367f0; }
  &--closed { color: #82868b; }
}

.props-toggle {
  padding: 2px 8px;
  font-size: 0.8rem;
  white-space: nowrap;
}

.prop-details {
  padding: 12px 14px;
  background: #f8f8fb;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.followup-panel {
  background: #fff;
  border: 1px solid #f0d9d9;
  border-left: 3px solid #ea5455;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.followup-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  color: #5e5873;
}

.followup-title {
  font-weight: 700;
  font-size: 0.82rem;
}

.followup-hint {
  font-size: 0.74rem;
  color: #6e6b7b;
}

.followup-audit {
  margin-left: auto;
  font-size: 0.72rem;
  color: #a8a5b8;
  white-space: nowrap;
}

.followup-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.followup-select {
  width: 150px;
  flex: 0 0 auto;
}

.followup-note {
  flex: 1 1 220px;
  min-width: 180px;
}

.owners-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #6e6b7b;
}

.prop-card {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-left: 3px solid #d8d6e0;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &--interested {
    border-left-color: #7367f0;
    background: #f6f5ff;
  }
}

.prop-card-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
}

.prop-code {
  flex: 0 0 auto;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #5e5873;
  background: #ececf3;
  border-radius: 4px;
  padding: 3px 8px;
  transition: background-color 0.15s, color 0.15s;

  &--muted {
    color: #a8a5b8;
    font-weight: 600;
  }
}

a.prop-code:hover {
  color: #fff;
  background: #80AC03;
  text-decoration: none;
}

.prop-name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  font-weight: 600;
  font-size: 0.98rem;
  color: #5e5873;
}
a.prop-name:hover { text-decoration: underline; }

.prop-kind {
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 0.74rem;
  font-weight: 600;
}

.prop-email {
  flex: 0 0 auto;
  font-size: 0.74rem;
  font-weight: 600;
}

.prop-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.prop-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 600;
  color: #6e6b7b;
  background: #f2f2f6;
  border-radius: 4px;
  padding: 2px 7px;

  &--sale {
    color: #286b45;
    background: #e6f5ec;
  }
  &--rent {
    color: #0f6b8c;
    background: #e2f2f8;
  }
  &--empty {
    color: #a8a5b8;
    background: transparent;
    padding-left: 0;
    font-weight: 400;
  }
  &--stale {
    color: #b5451b;
    background: #fdece4;
  }
  &--sm {
    font-size: 0.74rem;
    font-weight: 500;
    padding: 1px 6px;
  }
}

.prop-card-owner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding-top: 6px;
  border-top: 1px dashed #ebe9f1;
  font-size: 0.82rem;

  &--empty { font-style: italic; }
}

.prop-owner-ico { color: #a8a5b8; }

.prop-owner-name {
  font-weight: 600;
  color: #5e5873;

  &--link:hover {
    color: #80AC03;
    text-decoration: underline;
  }
}

.prop-owner-has {
  display: inline-flex;
  align-items: center;
  color: #a8a5b8;

  &:hover { color: #80AC03; }
}

.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
